import * as yup from 'yup';

import { Alert, Card, CardBody, Col, Form, FormGroup, Row } from 'reactstrap';
import { NavLink, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../../store';

import Button from '../../components/Button';
import { IAuthState } from '../../store/auth/Types';
import { IPasswordResetCompleteForm } from '../../services/Auth';
import Input from '../../components/Input';
import Logo from '../../assets/images/logo-blue.png';
import React, { useCallback } from 'react';
import { completePasswordReset } from '../../store/auth/Actions';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

type Props = {
    match: any;
};

// The server encodes the reset token as Base64Url (replacing + with -, / with _, stripping = padding)
// to safely embed it in a URL path segment without slash-splitting issues.
// This function converts it back to the standard Base64 string the Identity API expects.
function base64UrlToToken(base64Url: string): string {
    // Re-pad to a multiple of 4 characters
    const padded = base64Url + '==='.slice((base64Url.length + 3) % 4);
    // Convert Base64Url chars back to standard Base64 chars
    return padded.replace(/-/g, '+').replace(/_/g, '/');
}

const PasswordResetComplete = (props: Props) => {
    const rawToken = base64UrlToToken(props.match.params.token);
    const passwordResetCompleteSchema = yup.object<IPasswordResetCompleteForm>({
        username: yup.string().required(`Email is required`),
        token: yup.string().default(rawToken).defined(),
        password: yup
            .string()
            .min(12, `Your password must be at least 12 characters`)
            .matches(/[A-Z]/, `Your password must include an uppercase letter`)
            .matches(/[a-z]/, `Your password must include a lowercase letter`)
            .matches(/[0-9]/, `Your password must include a number`)
            .matches(/[^A-Za-z0-9]/, `Your password must include a special character`)
            .required(`Your password must be at least 12 characters`),
        confirmPassword: yup
            .string()
            .test('match', 'Passwords do not match', function (confirmPassword) {
                if (this?.options['parent']?.password) {
                    return confirmPassword === this?.options['parent']?.password;
                }

                return false;
            })
            .required(),
    });

    const auth = useSelector((state: any) => state.auth);
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
    } = useForm<IPasswordResetCompleteForm>({
        resolver: yupResolver(passwordResetCompleteSchema as any),
        mode: 'onChange',
        defaultValues: {
            username: '',
            token: rawToken,
            password: '',
            confirmPassword: '',
        },
    });
    const dispatch = useDispatch<AppDispatch>();

    const onSubmit = useCallback(
        (formData: IPasswordResetCompleteForm) => {
            dispatch(completePasswordReset(formData));
        },
        [dispatch],
    );

    return (
        <>
            {auth.authenticated && <Redirect to="/profile" />}
            <Row className="justify-content-center">
                <Col md="8" lg="6" xl="5">
                    <Card>
                        <CardBody>
                            <div className="text-center w-75 m-auto">
                                <NavLink to="/">
                                    <span>
                                        <img src={Logo} alt="logo" height="150" />
                                    </span>
                                </NavLink>
                                <p className="text-muted mb-4 mt-3">
                                    Please complete the form below to complete your password reset request.
                                </p>
                            </div>

                            {auth.passwordReset && (
                                <Alert color={'success'} transition={{ timeout: 0 }}>
                                    Your password has been successfully reset. Please click the following link to login:{' '}
                                    <NavLink to="/login">Go to the Login Page</NavLink>
                                </Alert>
                            )}

                            {!auth.passwordReset && auth.passwordResetError && (
                                <Alert color={'danger'} transition={{ timeout: 0 }}>
                                    {auth.passwordResetError}
                                </Alert>
                            )}

                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <FormGroup className="mb-3">
                                    <label htmlFor="username">Confirm Your Email</label>
                                    <Controller
                                        name="username"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                className="form-control"
                                                type="email"
                                                id="username"
                                                placeholder="Confirm your email"
                                            />
                                        )}
                                    />
                                    {errors.username && <span className="text-danger">{errors.username.message}</span>}
                                </FormGroup>

                                <Alert
                                    color={'primary'}
                                    transition={{ timeout: 0 }}
                                >{`Passwords must be at least 12 characters and include uppercase, lowercase, a number, and a special character.`}</Alert>

                                <FormGroup className="mb-3">
                                    <label htmlFor="password">New Password</label>
                                    <Controller
                                        name="password"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                className="form-control"
                                                type="password"
                                                id="password"
                                                placeholder="Enter a new password"
                                            />
                                        )}
                                    />
                                    {errors.password && <span className="text-danger">{errors.password.message}</span>}
                                </FormGroup>
                                <FormGroup className="mb-3">
                                    <label htmlFor="confirmPassword">Confirm New Password</label>
                                    <Controller
                                        name="confirmPassword"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                className="form-control"
                                                type="password"
                                                id="confirmPassword"
                                                placeholder="Confirm the new password"
                                            />
                                        )}
                                    />
                                    {errors.confirmPassword && (
                                        <span className="text-danger">{errors.confirmPassword.message}</span>
                                    )}
                                    {errors.token && <span className="text-danger">{errors.token.message}</span>}
                                </FormGroup>
                                <FormGroup className="mb-0 text-center">
                                    <Button className="btn-block" type="submit">
                                        Complete Password Reset
                                    </Button>
                                </FormGroup>
                            </Form>
                        </CardBody>
                    </Card>

                    <Row className="mt-3">
                        <Col xs={12} className="text-center">
                            <p>
                                <NavLink to="/login" className="ms-1">
                                    Back to Login
                                </NavLink>
                            </p>
                        </Col>
                        <Col xs={12} className="text-center">
                            <p>
                                <NavLink to="/passwordreset/request" className="ms-1">
                                    Request a new Link
                                </NavLink>
                            </p>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    );
};

export default PasswordResetComplete;
